import React from 'react';
import { Col, Row, Button } from 'reactstrap';
import Link from 'next/link';
import domainModel from 'domain-model';
import type { CartRow as CartRowType } from 'domain-model/types/pcs';
import type { Article } from 'domain-model/types/oms';
// @ts-expect-error this file is in shop and purchasing respectively
// eslint-disable-next-line import/no-unresolved
import composeUrl from 'app/config/compose-url';
import Money from '../Money';
import IconX from '../icons/IconX';
import Image from '../Image';
import ConnectedArticleQuantityInputJustButtons from '../inputs/ConnectedArticleQuantityInputJustButtons';
import CartArticleQuantityInputNew from './CartArticleQuantityInputNew';
import DailyPriceIcon from '../article/DailyPriceIcon';
import PriceSpan from '../PriceSpan';
import GridCol from '../articleQuantityComponents/GridCol';

function CartRow({
  explicitCartKey,
  cartRow,
  handleRemoveItem,
  onLinkClick,
  isGothiaMode,
  cannotBeDelivered,
  earliestPossibleDeliveryDate,
  disableQuantityInput,
}: {
  explicitCartKey?: string;
  cartRow: CartRowType;
  handleRemoveItem: Function;
  onLinkClick: Function;
  isGothiaMode: boolean;
  cannotBeDelivered?: boolean;
  earliestPossibleDeliveryDate?: string;
  disableQuantityInput?: boolean;
}) {
  const { pricedArticle, procurementArticle } = cartRow;
  const total = domainModel.cart.getCartTotal({ cartRows: [cartRow] }) || 0;

  const articleViewModel = domainModel.pricedArticle.getViewModel(
    pricedArticle,
  ) as Article;

  const { shortName, isAvailableForPreorder, numberOfExtraDeliveryDays } =
    articleViewModel;
  const { minimumQuantity, isPricedArticleStale } = cartRow;
  const articleIsStaleOrMissingOfc =
    isPricedArticleStale || !articleViewModel.myOrderFullfillmentCenter;

  const articleUrl = procurementArticle
    ? composeUrl('procurement-article', procurementArticle)
    : composeUrl('article', articleViewModel);

  let rowClassName = '';
  if (earliestPossibleDeliveryDate && cannotBeDelivered) {
    rowClassName = 'text-muted border border-danger my-1';
  } else if (articleIsStaleOrMissingOfc) {
    rowClassName = 'text-muted border border-warning my-1';
  } else if (!cannotBeDelivered) {
    rowClassName = '';
  } else if (numberOfExtraDeliveryDays && numberOfExtraDeliveryDays > 0) {
    rowClassName = 'border border-warning my-1';
  }

  return (
    <div className={`py-3 ${rowClassName}`}>
      <Row className="align-items-stretch justify-content-between g-0 p-1">
        <Col className="d-none d-md-flex align-items-center" md="auto">
          <Image src={articleViewModel.image} resY={75} resX={75} />
        </Col>
        <Col xs={isGothiaMode ? 3 : 4}>
          <Row className="align-items-space-around h-100 g-0">
            <Col xs={12} className="mt-auto">
              {procurementArticle && procurementArticle.name && (
                <p
                  style={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                  className="m-0"
                >
                  <strong>{procurementArticle.name}</strong>
                </p>
              )}
              {articleIsStaleOrMissingOfc && <strong>{shortName}</strong>}
              {!articleIsStaleOrMissingOfc && (
                <p
                  style={{
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                  className="text-primary m-0"
                  onClick={() => onLinkClick()}
                >
                  <Link href={articleUrl} className="text-primary text-nowrap">
                    <strong>{shortName}</strong>
                  </Link>
                </p>
              )}
            </Col>
            <Col xs={12} className="mb-auto">
              <span className="d-block text-xs text-muted">
                {articleViewModel.brand?.name}
              </span>
              {isAvailableForPreorder && (
                <span className="d-block text-xs text-warning">
                  <small>Förbokning</small>
                </span>
              )}
              {earliestPossibleDeliveryDate && (
                <span
                  className={
                    cannotBeDelivered
                      ? 'd-block text-xs text-danger'
                      : 'd-block text-xs text-muted'
                  }
                >
                  <small>
                    Denna artikel kan levereras som tidigast{' '}
                    {earliestPossibleDeliveryDate}
                  </small>
                </span>
              )}
              {!earliestPossibleDeliveryDate &&
                numberOfExtraDeliveryDays &&
                numberOfExtraDeliveryDays > 0 && (
                  <span className="d-block text-xs text-warning">
                    <small>
                      Denna artikel har en förlängd leveranstid på{' '}
                      {numberOfExtraDeliveryDays} dagar
                    </small>
                  </span>
                )}
              {articleViewModel.hasLongerDeliveryTime && (
                <span className="d-block text-xs text-warning">
                  <small>Beställningsvara - längre ledtid</small>
                </span>
              )}
            </Col>
          </Row>
        </Col>
        {articleIsStaleOrMissingOfc && (
          <Col xs={6} md={isGothiaMode ? 5 : 4} className="mt-auto">
            <p>Artikeln ej längre tillgänglig för köp</p>
          </Col>
        )}
        {!articleIsStaleOrMissingOfc && (
          <>
            <Col xs={6} md={isGothiaMode ? 6 : 4}>
              <Row className="connectedArticleQuantityInput align-items-center g-0 w-100 cartRow">
                {!isGothiaMode && (
                  <>
                    <GridCol
                      xs={6}
                      className="displayOrderUnitSize-cartRow"
                      children={articleViewModel.displayOrderUnitSize as string}
                    />
                    <GridCol
                      xs={6}
                      children={
                        <>
                          <PriceSpan pricedArticle={articleViewModel} />
                          <DailyPriceIcon article={articleViewModel} />
                        </>
                      }
                    />
                  </>
                )}
                <Col xs={12}>
                  {isGothiaMode && (
                    <CartArticleQuantityInputNew
                      explicitCartKey={explicitCartKey}
                      cartRow={cartRow}
                      // @ts-ignore
                      article={articleViewModel}
                      procurementArticle={procurementArticle}
                      disabled={disableQuantityInput}
                      dontShowSKUSwitch
                    />
                  )}
                  {!isGothiaMode && (
                    <ConnectedArticleQuantityInputJustButtons
                      cartRow={cartRow}
                      article={articleViewModel}
                      procurementArticle={procurementArticle}
                      disabled={disableQuantityInput}
                    />
                  )}
                </Col>
              </Row>
            </Col>
          </>
        )}
        <Col xs={1} className="w-auto">
          <Row className="align-items-space-around h-100 g-0">
            {!disableQuantityInput && (
              <Col className="my-auto text-start" xs={12}>
                <Button
                  onClick={() => handleRemoveItem()}
                  role="button"
                  title="Ta bort vara"
                  tabIndex={0}
                  color="none"
                  aria-label="Close"
                  className={`close-rotate p-1 ${minimumQuantity && 'd-none'}`}
                >
                  <IconX height="16" />
                </Button>
              </Col>
            )}

            {!isGothiaMode && (
              <Col className="text-sm my-auto text-end">
                {!articleIsStaleOrMissingOfc && (
                  <small>
                    <Money
                      currency={articleViewModel.currency}
                      value={total}
                    ></Money>
                  </small>
                )}
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </div>
  );
}

export default CartRow;
